import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RTKSideEffectSchema } from "../index";

// The tenant's custom-field allowance, used until /custom-fields-limit answers and whenever it
// cannot be read. Lives with the slice because both screens that cap custom fields — the list
// section on /branding/forms and the builder's field library — now read the same number from the
// store instead of each carrying their own literal, which is how the two used to drift.
export const DEFAULT_CUSTOM_FIELD_LIMIT = 15;

export interface CustomFieldLimitState extends RTKSideEffectSchema {
  Data: number;
}

const initialState: CustomFieldLimitState = {
  Data: DEFAULT_CUSTOM_FIELD_LIMIT
};

interface GetCustomFieldLimitAPIResponse {
  CustomFieldLimit?: number;
}

export const CustomFieldLimitSlice = createSlice({
  name: "customFieldLimit",
  initialState,
  reducers: {
    getCustomFieldLimitRequest(state) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isError = false;
      state.error = "";
    },
    getCustomFieldLimitSuccess(state, action: PayloadAction<GetCustomFieldLimitAPIResponse>) {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      // ?? and not ||: a plan that allows no custom fields answers with 0, and `||` treated that as
      // "nothing came back" and stored the fallback instead. The tenant then saw "0 / 15", isAtLimit
      // stayed false, and both create entry points stayed live — the builder's dialog trigger reads
      // the same number (AddCustomField) — so the only thing refusing the create was the server.
      // Nullish coalescing keeps 0 and still falls back when the field is absent or null.
      state.Data = action.payload?.CustomFieldLimit ?? DEFAULT_CUSTOM_FIELD_LIMIT;
    },
    getCustomFieldLimitFailure(state, action: PayloadAction<string>) {
      // No Toastify here, unlike the sibling slices in this folder. A limit we could not read must
      // not put an error in front of someone who was only looking at the list — the endpoint is
      // gated on API_ViewConfiguration, so a viewer without it gets a failure on every visit. Data
      // stays at the fallback, the create call is authoritative, and the server rejects an
      // over-limit field on its own.
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload;
    }
  }
});

export const { getCustomFieldLimitRequest, getCustomFieldLimitSuccess, getCustomFieldLimitFailure } = CustomFieldLimitSlice.actions;
